<script setup lang="ts">
    import { ref, watch } from "vue";
    import { defaultAutocompleteConverter } from "@/components/forms/inputs/Autocomplete/helpers";
    import { useStore } from "@/store";
    import { flashWarning } from "@/utils";

    const props = withDefaults(
        defineProps<{
            label?: string;
            idrottId?: string;
            spelareId?: string;
            includeAllOption?: boolean;
        }>(),
        { label: "Lag", includeAllOption: false }
    );

    const store = useStore();

    const fetchLag = async ({ query }: { query?: string } = {}) => {
        try {
            const params: { namn?: string; idrott?: string; spelare?: string } = {};
            if (query) params.namn = query;
            if (props.idrottId) params.idrott = props.idrottId;
            if (props.spelareId) params.spelare = props.spelareId;

            const { subsetName } = await store.dispatch("skreg/lag/fetchLag", { filterParams: params });
            const items = store.getters["skreg/lag/lagSubset"]({ subsetName, limit: 0 });
            const options = items.map((lag: { id: string; namn: string }) => ({ value: lag.id, label: lag.namn }));

            return props.includeAllOption ? [{ value: "allalag", label: "Alla lag" }, ...options] : options;
        } catch {
            flashWarning("Kunde inte hämta lag");
            return [];
        }
    };

    // KAutocomplete anropar en funktions-`options` bara vid mount. För att dropdownen ska följa
    // idrott-/spelarfiltret matar vi options som en reaktiv array och hämtar om när filtret ändras.
    const options = ref<{ value: string; label: string }[]>([]);

    const refreshOptions = async () => {
        options.value = await fetchLag();
    };

    watch([() => props.idrottId, () => props.spelareId], refreshOptions);

    /*
        Första hämtningen görs explicit i stället för med `immediate: true` på watchern ovan.
        Vue 2.7 initierar `oldValue` till [] för watchers med array-källa, så immediate-jobbet jämför
        undefined mot undefined för varje källa och hoppar över callbacken helt när ingen av
        propparna är satt. Fältet blev då tomt överallt där varken idrottId eller spelareId skickas.
     */
    refreshOptions();
</script>

<template>
    <KAutocomplete
        name="lag"
        :label="label"
        v-bind="$attrs"
        v-on="$listeners"
        :options="options"
        :onSearch="fetchLag"
        :converter="defaultAutocompleteConverter"
    />
</template>

<style lang="scss" scoped></style>
